/**
 * RequestRejectModal
 * 
 * Modal for capturing rejection reason when rejecting a request.
 * Shows categorized rejection reasons and allows "Other" as a last resort.
 */

import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { ScrollArea } from '../ui/scroll-area';
import { cn } from '../../lib/utils';
import { 
  Clock, 
  Target, 
  Wrench, 
  HelpCircle,
  Check,
  Calendar,
  Code,
  Rocket,
  ParkingCircle,
  Copy,
  Compass,
  TrendingUp,
  Users,
  Layers,
  Sparkles,
  AlertTriangle,
  DollarSign,
  Shield,
  Link,
  FileQuestion,
  Lightbulb,
  XCircle,
  MessageSquare
} from 'lucide-react';

const REJECTION_CATEGORIES = [
  {
    id: 'timing',
    label: 'Timing & Status',
    description: 'The "Not Now" Reasons - Valid idea, wrong timing',
    icon: Clock,
    color: 'text-blue-500',
    bgColor: 'bg-blue-50 dark:bg-blue-950/30',
    reasons: [
      { id: 'already_planned', label: 'Already Planned', description: 'On the roadmap for a future sprint', icon: Calendar },
      { id: 'in_development', label: 'In Development', description: 'Currently being built by the engineering team', icon: Code },
      { id: 'already_released', label: 'Already Released/Fixed', description: 'The feature exists or the bug was squashed in a recent update', icon: Rocket },
      { id: 'parked', label: 'Parked / Backlogged', description: 'Valid idea, but no capacity to address it in the next 6–12 months', icon: ParkingCircle },
      { id: 'duplicate', label: 'Duplicate', description: 'Someone else already asked for this; tracking it under the original ticket', icon: Copy },
    ]
  },
  {
    id: 'strategic',
    label: 'Strategic Alignment',
    description: 'The "Product Vision" Reasons - Direction of product',
    icon: Target,
    color: 'text-purple-500',
    bgColor: 'bg-purple-50 dark:bg-purple-950/30',
    reasons: [
      { id: 'out_of_scope', label: 'Out of Scope / Non-Core', description: "Useful for someone, but doesn't fit the product's core mission", icon: Compass },
      { id: 'not_aligned', label: 'Not Aligned with Strategy', description: 'Contradicts the current quarterly or yearly goals', icon: TrendingUp },
      { id: 'wrong_persona', label: 'Targeting Wrong Persona', description: "Solves a problem for a user type you aren't currently optimizing for", icon: Users },
      { id: 'negative_ux', label: 'Negative Impact on UX', description: 'Would create too much "feature creep" or clutter the interface', icon: Layers },
      { id: 'niche_request', label: 'Niche Request', description: 'Too specific to one customer; lacks broad market appeal', icon: Sparkles },
    ]
  },
  {
    id: 'technical',
    label: 'Technical & Feasibility',
    description: 'The "Can\'t Do" Reasons - Reality and resources',
    icon: Wrench,
    color: 'text-amber-500',
    bgColor: 'bg-amber-50 dark:bg-amber-950/30',
    reasons: [
      { id: 'technical_constraint', label: 'Technical Constraint', description: 'Current architecture cannot support this without a massive rewrite', icon: AlertTriangle },
      { id: 'low_roi', label: 'Low ROI', description: 'The effort required to build it outweighs the potential value or revenue', icon: DollarSign },
      { id: 'security_risk', label: 'Security/Compliance Risk', description: 'Would violate data privacy, SOC2, or other safety standards', icon: Shield },
      { id: 'dependency_blocked', label: 'Dependency Blocked', description: "Requires a third-party API or internal service that doesn't exist yet", icon: Link },
    ]
  },
  {
    id: 'quality',
    label: 'Quality & Clarity',
    description: 'The "Need More" Reasons - Refinement needed',
    icon: HelpCircle,
    color: 'text-green-500',
    bgColor: 'bg-green-50 dark:bg-green-950/30',
    reasons: [
      { id: 'insufficient_info', label: 'Insufficient Information', description: 'Not enough detail to understand the "Why" or the "What"', icon: FileQuestion },
      { id: 'problem_solved', label: 'Problem Already Solved', description: "The user's pain point can be addressed with existing features (User Education needed)", icon: Lightbulb },
      { id: 'will_not_fix', label: 'Will Not Fix', description: "Usually for minor bugs or edge cases that don't justify the dev time", icon: XCircle },
    ]
  }
];

const RequestRejectModal = ({ 
  open, 
  onClose, 
  onConfirm, 
  requestTitle 
}) => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedReason, setSelectedReason] = useState(null);
  const [otherReason, setOtherReason] = useState('');
  const [isOther, setIsOther] = useState(false);

  const handleCategorySelect = (categoryId) => {
    setSelectedCategory(categoryId);
    setSelectedReason(null);
    setIsOther(false);
  };

  const handleReasonSelect = (reasonId) => {
    setSelectedReason(reasonId);
    setIsOther(false);
  };

  const handleOtherSelect = () => {
    setIsOther(true);
    setSelectedReason(null);
  };

  const handleConfirm = () => {
    if (isOther && otherReason.trim()) {
      onConfirm({
        rejection_category: 'other',
        rejection_reason: otherReason.trim()
      });
    } else if (selectedReason) {
      const category = REJECTION_CATEGORIES.find(c => c.id === selectedCategory);
      const reason = category?.reasons.find(r => r.id === selectedReason);
      onConfirm({
        rejection_category: selectedCategory,
        rejection_reason: reason?.label || selectedReason
      });
    }
    
    // Reset state
    setSelectedCategory(null);
    setSelectedReason(null);
    setOtherReason('');
    setIsOther(false);
  };

  const handleClose = () => {
    setSelectedCategory(null);
    setSelectedReason(null);
    setOtherReason('');
    setIsOther(false);
    onClose();
  };

  const canConfirm = (selectedReason && selectedCategory) || (isOther && otherReason.trim().length > 0);
  const activeCategory = REJECTION_CATEGORIES.find(c => c.id === selectedCategory);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <XCircle className="w-5 h-5 text-red-500" />
            Reject Request
          </DialogTitle>
          <DialogDescription>
            {requestTitle ? (
              <>Why are you rejecting "<span className="font-medium">{requestTitle}</span>"?</>
            ) : (
              'Select a reason for rejecting this request'
            )}
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="flex-1 pr-4 -mr-4">
          <div className="space-y-4 py-2">
            {/* Category Selection */}
            {!selectedCategory && (
              <div className="grid grid-cols-2 gap-3">
                {REJECTION_CATEGORIES.map((category) => {
                  const Icon = category.icon;
                  return (
                    <button
                      key={category.id}
                      onClick={() => handleCategorySelect(category.id)}
                      className={cn(
                        "p-4 rounded-lg border text-left transition-all hover:shadow-md",
                        "border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600",
                        category.bgColor
                      )}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Icon className={cn("w-5 h-5", category.color)} />
                        <span className="font-medium text-sm">{category.label}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">{category.description}</p>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Reason Selection */}
            {selectedCategory && activeCategory && (
              <div className="space-y-3">
                {/* Back button and category header */}
                <div className="flex items-center gap-2 pb-2 border-b">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setSelectedCategory(null)}
                    className="h-7 px-2"
                  >
                    ← Back
                  </Button>
                  <div className={cn("flex items-center gap-2 px-2 py-1 rounded-md", activeCategory.bgColor)}>
                    <activeCategory.icon className={cn("w-4 h-4", activeCategory.color)} />
                    <span className="text-sm font-medium">{activeCategory.label}</span>
                  </div>
                </div>

                {/* Reason options */}
                <div className="space-y-2">
                  {activeCategory.reasons.map((reason) => {
                    const ReasonIcon = reason.icon;
                    const isSelected = selectedReason === reason.id;
                    
                    return (
                      <button
                        key={reason.id}
                        onClick={() => handleReasonSelect(reason.id)}
                        className={cn(
                          "w-full p-3 rounded-lg border text-left transition-all",
                          "hover:bg-gray-50 dark:hover:bg-gray-800",
                          isSelected 
                            ? "border-blue-500 bg-blue-50 dark:bg-blue-950/30 ring-1 ring-blue-500" 
                            : "border-gray-200 dark:border-gray-700"
                        )}
                      >
                        <div className="flex items-start gap-3">
                          <div className={cn(
                            "mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors",
                            isSelected 
                              ? "border-blue-500 bg-blue-500 text-white" 
                              : "border-gray-300 dark:border-gray-600"
                          )}>
                            {isSelected && <Check className="w-3 h-3" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              <ReasonIcon className="w-4 h-4 text-muted-foreground" />
                              <span className="font-medium text-sm">{reason.label}</span>
                            </div>
                            <p className="text-xs text-muted-foreground">{reason.description}</p>
                          </div>
                        </div>
                      </button>
                    );
                  })}

                  {/* Other option */}
                  <button
                    onClick={handleOtherSelect}
                    className={cn(
                      "w-full p-3 rounded-lg border text-left transition-all",
                      "hover:bg-gray-50 dark:hover:bg-gray-800",
                      isOther 
                        ? "border-blue-500 bg-blue-50 dark:bg-blue-950/30 ring-1 ring-blue-500" 
                        : "border-gray-200 dark:border-gray-700"
                    )}
                  >
                    <div className="flex items-start gap-3">
                      <div className={cn(
                        "mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors",
                        isOther 
                          ? "border-blue-500 bg-blue-500 text-white" 
                          : "border-gray-300 dark:border-gray-600"
                      )}>
                        {isOther && <Check className="w-3 h-3" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <MessageSquare className="w-4 h-4 text-muted-foreground" />
                          <span className="font-medium text-sm">Other Reason</span>
                        </div>
                        <p className="text-xs text-muted-foreground">Provide a custom rejection reason</p>
                      </div>
                    </div>
                  </button>

                  {/* Other reason text input */}
                  {isOther && (
                    <div className="pl-8 pt-2">
                      <Textarea
                        placeholder="Enter your rejection reason..."
                        value={otherReason}
                        onChange={(e) => setOtherReason(e.target.value)}
                        className="min-h-[80px]"
                        autoFocus
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        <DialogFooter className="mt-4 pt-4 border-t">
          <Button variant="outline" onClick={handleClose}>Cancel</Button>
          <Button 
            onClick={handleConfirm} 
            disabled={!canConfirm}
            variant="destructive"
          >
            Reject Request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RequestRejectModal;
